import i18n from "../i18n";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { Suspense, createContext, useEffect, useState } from "react";
import { initReactI18next, useTranslation } from "react-i18next";
import { SupportedLanguageCode, TranslationKeys } from "../types/types";
import {
  LOCAL_STORAGE_DATA,
  LocalStorageController,
} from "../utils/LocalStorageController";
import { isNotNullOrUndefined } from "../utils/isNotNullOrUndefined";
import LoadingIndicator from "../components/LoadingIndicator";

const TranslationProviderContext = createContext({
  getTranslationText: (key: keyof TranslationKeys, data?: {}): any => {},
  updateAppLanguageCode: async (
    newLanguageCode: SupportedLanguageCode
  ): Promise<void> => {},
  appLanguageCode: "" as SupportedLanguageCode,
});

// Normalize language codes like "en-GB" to "en"
const normalizeLanguageCode = (code: string): SupportedLanguageCode => {
  const baseCode = code.split("-")[0].toLowerCase();
  const supportedCodes: SupportedLanguageCode[] = ["en", "fr", "vi", "zh", "de", "ko", "id"];
  if (supportedCodes.includes(baseCode as SupportedLanguageCode)) {
    return baseCode as SupportedLanguageCode;
  }
  return "en"; // Default fallback
};

// Initialize i18n only once
let isI18nInitialized = false;

const initializeI18n = async (languageCode: SupportedLanguageCode) => {
  if (!isI18nInitialized) {
    await i18n
      .use(Backend)
      .use(I18nextBrowserLanguageDetector)
      .use(initReactI18next)
      .init({
        lng: languageCode,
        fallbackLng: "en",
        debug: false,
        interpolation: {
          escapeValue: false,
        },
      });
    isI18nInitialized = true;
  } else {
    // If already initialized, just change the language
    await i18n.changeLanguage(languageCode);
  }
};

export const TranslationProvider = ({ children }: { children: any }) => {
  const { t } = useTranslation();
  const [appLanguageCode, setAppLanguageCode] =
    useState<SupportedLanguageCode>("en");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    (async () => {
      // Get saved language or detect from browser
      let languageCode: SupportedLanguageCode = "en";
      
      const localLanguageCode = LocalStorageController.connect().getData(
        LOCAL_STORAGE_DATA.APP_LANGUAGE_CODE,
        "string"
      );

      if (isNotNullOrUndefined(localLanguageCode)) {
        languageCode = normalizeLanguageCode(localLanguageCode as string);
      } else {
        // If no saved language, detect from browser and normalize
        const browserLang = navigator.language || "en";
        languageCode = normalizeLanguageCode(browserLang);
      }
      
      setAppLanguageCode(languageCode);
      await initializeI18n(languageCode);
      setIsReady(true);
    })();
  }, []);

  const getTranslationText = (key: keyof TranslationKeys | any, data?: {}) => {
    return isNotNullOrUndefined(t) ? t!(key, data || {}) : "";
  };
  
  const updateAppLanguageCode = async (
    newLanguageCode: SupportedLanguageCode
  ) => {
    // Save to localStorage first
    LocalStorageController.connect().storeData(
      LOCAL_STORAGE_DATA.APP_LANGUAGE_CODE,
      newLanguageCode
    );
    // Change the i18n language
    await i18n.changeLanguage(newLanguageCode);
    // Update state
    setAppLanguageCode(newLanguageCode);
  };
  
  if (!isReady) {
    return <LoadingIndicator />;
  }
  
  return (
    <TranslationProviderContext.Provider
      value={{ getTranslationText, updateAppLanguageCode, appLanguageCode }}
    >
      <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    </TranslationProviderContext.Provider>
  );
};
export default TranslationProviderContext;

